<template>
  <div class="gender-ei-level-compare-chart">
    <div class="gender-ei-level-compare-chart__header">
      <h3 class="gender-ei-level-compare-chart__title">
        {{ title }}
      </h3>

      <div class="gender-ei-level-compare-chart__legend">
        <div class="gender-ei-level-compare-chart__legend-item">
          <span
              class="gender-ei-level-compare-chart__legend-mark"
              :style="{ backgroundColor: firstColor }"
          ></span>

          <span>{{ firstLabel }}</span>
        </div>

        <div class="gender-ei-level-compare-chart__legend-item">
          <span
              class="gender-ei-level-compare-chart__legend-mark"
              :style="{ backgroundColor: secondColor }"
          ></span>

          <span>{{ secondLabel }}</span>
        </div>
      </div>
    </div>

    <div class="gender-ei-level-compare-chart__body">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
} from 'chart.js'
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip
)

const valueLabelPlugin = {
  id: 'genderValueLabelPlugin',

  afterDatasetsDraw(chart) {
    const { ctx } = chart

    ctx.save()
    ctx.font = '600 10px sans-serif'
    ctx.fillStyle = '#ffffff'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)

      meta.data.forEach((bar, index) => {
        const value = dataset.data[index]

        if (value === null || value === undefined) {
          return
        }

        const isZero = Number(value) === 0
        const centerY = bar.y + ((bar.base - bar.y) / 2)

        ctx.fillStyle = isZero ? 'rgba(0, 0, 0, 0.45)' : '#ffffff'
        ctx.fillText(String(value), bar.x, isZero ? bar.base - 8 : centerY)
      })
    })

    ctx.restore()
  },
}

export default {
  name: 'GenderEiLevelCompareChart',

  props: {
    title: {
      type: String,
      default: 'Сравнение на каждом уровне Общего ЭИ',
    },

    firstLabel: {
      type: String,
      default: 'Мужчины',
    },

    secondLabel: {
      type: String,
      default: 'Женщины',
    },

    firstColor: {
      type: String,
      default: '#7FA2F4',
    },

    secondColor: {
      type: String,
      default: '#F28FB1',
    },

    items: {
      type: Array,
      default: () => [],
    },

    maxValue: {
      type: Number,
      default: null,
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    const chartInstance = ref(null)

    const destroyChart = () => {
      if (chartInstance.value) {
        chartInstance.value.destroy()
        chartInstance.value = null
      }
    }

    const renderChart = () => {
      destroyChart()

      if (!chartCanvas.value) {
        return
      }

      const labels = props.items.map((item) => item.label)
      const firstValues = props.items.map((item) => Number(item.firstValue) || 0)
      const secondValues = props.items.map((item) => Number(item.secondValue) || 0)
      const firstColors = props.items.map((item) => item.firstColor || props.firstColor)
      const secondColors = props.items.map((item) => item.secondColor || props.secondColor)
      const maxFromValues = Math.max(...firstValues, ...secondValues, 0)
      const suggestedMax = props.maxValue || Math.ceil((maxFromValues + 8) / 5) * 5

      chartInstance.value = new Chart(chartCanvas.value, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: props.firstLabel,
              data: firstValues,
              backgroundColor: firstColors,
              borderRadius: 4,
              borderSkipped: false,
              minBarLength: 2,
              barPercentage: 0.9,
              categoryPercentage: 0.7,
            },
            {
              label: props.secondLabel,
              data: secondValues,
              backgroundColor: secondColors,
              borderRadius: 4,
              borderSkipped: false,
              minBarLength: 2,
              barPercentage: 0.9,
              categoryPercentage: 0.7,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          layout: {
            padding: {
              top: 4,
              right: 0,
              bottom: 0,
              left: 0,
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            datalabels: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => {
                  return `${context.dataset.label}: ${context.parsed.y}`
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.06)',
                drawBorder: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 11,
                },
              },
              border: {
                display: false,
              },
            },
            y: {
              beginAtZero: true,
              suggestedMax,
              grid: {
                display: true,
                color: 'rgba(0, 0, 0, 0.06)',
                drawBorder: false,
              },
              ticks: {
                precision: 0,
                stepSize: 5,
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 11,
                },
              },
              border: {
                display: false,
              },
            },
          },
        },
        plugins: [valueLabelPlugin],
      })
    }

    onMounted(() => {
      nextTick(renderChart)
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        () => [
          props.items,
          props.firstLabel,
          props.secondLabel,
          props.firstColor,
          props.secondColor,
          props.maxValue,
        ],
        () => {
          nextTick(renderChart)
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
    }
  },
}
</script>

<style scoped lang="scss">
.gender-ei-level-compare-chart {
  width: 100%;
}

.gender-ei-level-compare-chart__header {
  margin-bottom: 20px;
}

.gender-ei-level-compare-chart__title {
  margin: 0 0 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
  font-weight: 600;
  font-size: 16px;
}

.gender-ei-level-compare-chart__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.gender-ei-level-compare-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.gender-ei-level-compare-chart__legend-mark {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 2px;
}

.gender-ei-level-compare-chart__body {
  width: 100%;
  height: 240px;
}

@media (max-width: $mobile-breakpoint) {
  .gender-ei-level-compare-chart__body {
    height: 230px;
  }
}
</style>
